/** Formatting helpers shared by every page. All dates render in the viewer's local time. */

export const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

const intFmt = new Intl.NumberFormat('en-US');
const usdFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdFineFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const dateTimeFmt = new Intl.DateTimeFormat('zh-HK', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
const timeFmt = new Intl.DateTimeFormat('zh-HK', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
const relFmt = new Intl.RelativeTimeFormat('zh-HK', { numeric: 'auto', style: 'short' });

export const fmtInt = (n: number | null | undefined) => (n == null ? '—' : intFmt.format(n));
/** Sub-cent amounts keep four decimals so a single AI call is still visible. */
export const fmtUsd = (n: number | null | undefined) => (n == null ? '—' : n !== 0 && Math.abs(n) < 0.01 ? usdFineFmt.format(n) : usdFmt.format(n));
export const fmtPct = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toFixed(part === whole ? 0 : 1)}%` : '—');
export const fmtMs = (ms: number) => (ms >= 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${fmtInt(Math.round(ms))} ms`);
export const fmtSigned = (n: number) => (n > 0 ? `+${fmtInt(n)}` : fmtInt(n));

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dateTimeFmt.format(d);
}

export const fmtTime = (ms: number) => timeFmt.format(new Date(ms));

export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const s = Math.round((t - now) / 1000);
  const abs = Math.abs(s);
  if (abs < 45) return '剛剛';
  if (abs < 3600) return relFmt.format(Math.round(s / 60), 'minute');
  if (abs < 86_400) return relFmt.format(Math.round(s / 3600), 'hour');
  if (abs < 30 * 86_400) return relFmt.format(Math.round(s / 86_400), 'day');
  return fmtDateTime(iso);
}

export const humanize = (key: string) => key.replace(/[_.]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** The last `n` UTC days (YYYY-MM-DD), oldest first — matches the server's `day` buckets. */
export function lastUtcDays(n: number, now = Date.now()): string[] {
  return Array.from({ length: n }, (_, i) => new Date(now - (n - 1 - i) * 86_400_000).toISOString().slice(0, 10));
}
